import { useEffect, useRef, useState } from 'react';
import { devLog } from '../lib/devLog';
import { noteError } from '../lib/diagnostics';
import { VideoPlaceholder } from './VideoPlaceholder';

/**
 * A <video> that outlives the screen showing it.
 *
 * The kiosk's film assets played from an element created on mount, so every
 * visit to a screen fetched the file from the asset server and stood up a
 * decoder from nothing. On the panel that was ~0.7s per visit, spent showing
 * the WebView's grey placeholder and play symbol before the first frame — the
 * instruction step measured 3.8s for a 3.1s clip, every guest.
 *
 * Here each clip has one element for the life of the page. It is created and
 * preloaded once (`warmPersistentVideo`, called from the flow at boot for the
 * clip the attract screen does not show), parked in a hidden holder while no
 * screen wants it, and MOVED into the screen that does — a DOM move keeps the
 * element's buffered data and decoder state, so the first frame is already
 * there when it is appended and `play()` starts from it. Parking pauses and
 * rewinds; nothing is ever loaded twice.
 *
 * The web flow keeps VideoPlaceholder: a phone arrives with a network, plays
 * each clip once, and should not hold two decoded films in memory.
 *
 * If the element errors, the screen falls back to VideoPlaceholder, which
 * carries the retry and the guest-safe fallback. Nothing here is on the
 * recording path.
 */

interface Slot {
  el: HTMLVideoElement;
  src: string;
  failed: boolean;
}

const slots = new Map<string, Slot>();
let lot: HTMLDivElement | null = null;

/** Out of sight, in the document: a parked element keeps its media state. */
function parkingLot(): HTMLDivElement {
  if (lot) return lot;
  lot = document.createElement('div');
  lot.setAttribute('data-video-parking', 'true');
  lot.setAttribute('aria-hidden', 'true');
  Object.assign(lot.style, {
    position: 'fixed',
    left: '0',
    top: '0',
    width: '2px',
    height: '2px',
    overflow: 'hidden',
    opacity: '0',
    pointerEvents: 'none',
    zIndex: '-1',
  });
  document.body.appendChild(lot);
  return lot;
}

function describe(err: MediaError | null): string {
  if (!err) return 'error event';
  const names: Record<number, string> = {
    1: 'MEDIA_ERR_ABORTED',
    2: 'MEDIA_ERR_NETWORK',
    3: 'MEDIA_ERR_DECODE',
    4: 'MEDIA_ERR_SRC_NOT_SUPPORTED',
  };
  return `${names[err.code] ?? `code ${err.code}`}${err.message ? ` — ${err.message}` : ''}`;
}

/**
 * Create and start loading the element for `id`, or return the one that
 * exists. Safe to call early and often; the flow calls it at boot.
 */
export function warmPersistentVideo(id: string, src: string): HTMLVideoElement {
  const existing = slots.get(id);
  if (existing && existing.src === src) return existing.el;
  existing?.el.remove();

  const el = document.createElement('video');
  el.preload = 'auto';
  el.playsInline = true;
  el.muted = true;
  el.setAttribute('data-persistent-video', id);
  el.src = src;
  const slot: Slot = { el, src, failed: false };
  el.addEventListener('error', () => {
    slot.failed = true;
    noteError(`video.${id}`, `${src}: ${describe(el.error)}`);
  });
  el.addEventListener(
    'canplaythrough',
    () => devLog(`[video] ${id} preloaded (readyState ${el.readyState}, ${el.duration.toFixed(1)}s)`),
    { once: true },
  );
  parkingLot().appendChild(el);
  el.load();
  slots.set(id, slot);
  return el;
}

function park(slot: Slot): void {
  const { el } = slot;
  el.pause();
  try {
    el.currentTime = 0;
  } catch {
    /* no metadata yet — nothing to rewind */
  }
  el.className = '';
  el.loop = false;
  parkingLot().appendChild(el);
}

export function PersistentVideo({
  id,
  src,
  label,
  className,
  muted = true,
  loop = false,
  onEnded,
}: {
  /** Stable per clip; the element lives under this key for the page's life. */
  id: string;
  src: string;
  label: string;
  className: string;
  muted?: boolean;
  loop?: boolean;
  onEnded?: () => void;
}) {
  const hostRef = useRef<HTMLDivElement | null>(null);
  const [fallback, setFallback] = useState(false);
  const onEndedRef = useRef(onEnded);
  onEndedRef.current = onEnded;

  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;
    const el = warmPersistentVideo(id, src);
    const slot = slots.get(id);
    if (!slot || slot.failed) {
      setFallback(true);
      return;
    }

    el.className = className;
    el.loop = loop;
    el.muted = muted;
    host.appendChild(el);
    try {
      el.currentTime = 0;
    } catch {
      /* not seekable yet; play() starts from the head anyway */
    }

    const ended = (): void => onEndedRef.current?.();
    const errored = (): void => setFallback(true);
    el.addEventListener('ended', ended);
    el.addEventListener('error', errored);

    // Same rule as VideoPlaceholder: an unmuted clip that is refused plays
    // silent rather than not at all.
    void el.play().catch(() => {
      el.muted = true;
      void el.play().catch((err: unknown) => noteError('video.autoplay', err));
    });

    return () => {
      el.removeEventListener('ended', ended);
      el.removeEventListener('error', errored);
      park(slot);
    };
  }, [id, src, className, muted, loop]);

  if (fallback) {
    return (
      <VideoPlaceholder
        src={src}
        label={label}
        kiosk
        muted={muted}
        loop={loop}
        autoPlay
        onEnded={onEnded}
        className={className}
      />
    );
  }
  // `contents` makes no box of its own, so the video's own positioning classes
  // resolve against the screen exactly as a directly rendered <video> would.
  return <div ref={hostRef} className="contents" data-persistent-video-host={id} />;
}
